console.log("VITE_ENVIRONMENT", import.meta.env.VITE_ENVIRONMENT);
console.log("VITE_RUNNING_IN", import.meta.env.VITE_RUNNING_IN);

import { Amplify } from "aws-amplify";
import lxwConstants from "./lexworkplace-constants";
import legalworks_config from "./legalworks.frontend.config";

const localstackApis = lxwConstants.localstackApiIds.APIs;

/**
 * Build a LocalStack API Gateway endpoint URL
 * @param {string} apiId - The API ID (e.g., "lxw_localstack_api")
 * @returns {string} Full endpoint URL
 */
const buildLocalstackEndpoint = (apiId) =>
	`https://lexworkplace.local:4565/restapis/${apiId}/test/_user_request_`;

const cognitoUserPool =
	lxwConstants.cognitoUserPools.pools[legalworks_config.cognitoUserPool];

const amplifyAuthConfig = {
	Cognito: {
		...(cognitoUserPool.identityPoolId
			? { identityPoolId: cognitoUserPool.identityPoolId }
			: {}),
		region: cognitoUserPool.region,
		userPoolId: cognitoUserPool.userPoolId,
		userPoolClientId: cognitoUserPool.userPoolClientId,
		mandatorySignIn: true,
		loginWith: {
			username: true,
			email: true,
		},
	},
};

const endpoints = {
	production:
		"https://k7i5zuhixe.execute-api.us-east-2.amazonaws.com/production",
	staging: "https://k7i5zuhixe.execute-api.us-east-2.amazonaws.com/staging",
	development: "https://k7i5zuhixe.execute-api.us-east-2.amazonaws.com/Dev",
	newfeature:
		"https://k7i5zuhixe.execute-api.us-east-2.amazonaws.com/newfeature",
	newfeature2:
		"https://k7i5zuhixe.execute-api.us-east-2.amazonaws.com/newfeature2",
	onboarding:
		"https://k7i5zuhixe.execute-api.us-east-2.amazonaws.com/onboarding",

	// localstack: https://lexworkplace.local:4565/restapis/lxw_localstack_api/test/_user_request_
	localstack: buildLocalstackEndpoint(localstackApis.localstack),
	// localstack_alpha: https://lexworkplace.local:4565/restapis/lxw_localstack_alpha_api/test/_user_request_
	localstack_alpha: buildLocalstackEndpoint(localstackApis.localstack_alpha),
	// localstack_bravo: https://lexworkplace.local:4565/restapis/lxw_localstack_bravo_api/test/_user_request_
	localstack_bravo: buildLocalstackEndpoint(localstackApis.localstack_bravo),
	// localstack_charlie: https://lexworkplace.local:4565/restapis/lxw_localstack_charlie_api/test/_user_request_
	localstack_charlie: buildLocalstackEndpoint(
		localstackApis.localstack_charlie
	),
	// localstack_delta: https://lexworkplace.local:4565/restapis/lxw_localstack_delta_api/test/_user_request_
	localstack_delta: buildLocalstackEndpoint(localstackApis.localstack_delta),
};

const production_aws_config = {
	Auth: amplifyAuthConfig,
	API: {
		REST: {
			"production-authorized": {
				endpoint: endpoints.production,
			},
			"production-public": {
				endpoint: endpoints.production,
			},
		},
	},
};
const staging_aws_config = {
	Auth: amplifyAuthConfig,
	API: {
		REST: {
			"staging-authorized": {
				endpoint: endpoints.staging,
			},
			"staging-public": {
				endpoint: endpoints.staging,
			},
		},
	},
};
const dev_aws_config = {
	Auth: amplifyAuthConfig,
	API: {
		REST: {
			"development-authorized": {
				endpoint: endpoints.development,
			},
			"development-public": {
				endpoint: endpoints.development,
			},
		},
	},
};
const newfeature_aws_config = {
	Auth: amplifyAuthConfig,
	API: {
		REST: {
			"newfeature-authorized": {
				endpoint: endpoints.newfeature,
			},
			"newfeature-public": {
				endpoint: endpoints.newfeature,
			},
		},
	},
};
const newfeature2_aws_config = {
	Auth: amplifyAuthConfig,
	API: {
		REST: {
			"newfeature2-authorized": {
				endpoint: endpoints.newfeature2,
			},
			"newfeature2-public": {
				endpoint: endpoints.newfeature2,
			},
		},
	},
};
const local_aws_config = {
	Auth: amplifyAuthConfig,

	API: {
		// all endpoints are available locally, in case you want to talk to
		// various backends while running the frontend locally
		REST: {
			"localstack-authorized": {
				endpoint: endpoints.localstack,
			},
			"localstack-public": {
				endpoint: endpoints.localstack,
			},
			"localstack_alpha-authorized": {
				endpoint: endpoints.localstack_alpha,
			},
			"localstack_alpha-public": {
				endpoint: endpoints.localstack_alpha,
			},
			"localstack_bravo-authorized": {
				endpoint: endpoints.localstack_bravo,
			},
			"localstack_bravo-public": {
				endpoint: endpoints.localstack_bravo,
			},
			"localstack_charlie-authorized": {
				endpoint: endpoints.localstack_charlie,
			},
			"localstack_charlie-public": {
				endpoint: endpoints.localstack_charlie,
			},
			"localstack_delta-authorized": {
				endpoint: endpoints.localstack_delta,
			},
			"localstack_delta-public": {
				endpoint: endpoints.localstack_delta,
			},
			"development-authorized": {
				endpoint: endpoints.development,
			},
			"development-public": {
				endpoint: endpoints.development,
			},
			"newfeature-authorized": {
				endpoint: endpoints.newfeature,
			},
			"newfeature-public": {
				endpoint: endpoints.newfeature,
			},
			"newfeature2-authorized": {
				endpoint: endpoints.newfeature2,
			},
			"newfeature2-public": {
				endpoint: endpoints.newfeature2,
			},
			"staging-authorized": {
				endpoint: endpoints.staging,
			},
			"staging-public": {
				endpoint: endpoints.staging,
			},
			"production-authorized": {
				endpoint: endpoints.production,
			},
			"production-public": {
				endpoint: endpoints.production,
			},
			"onboarding-authorized": {
				endpoint: endpoints.onboarding,
			},
			"onboarding-public": {
				endpoint: endpoints.onboarding,
			},
		},
	},
};
const aws_config_for_environment = (() => {
	switch (import.meta.env.VITE_ENVIRONMENT) {
		case "production":
			return production_aws_config;
		case "staging":
			return staging_aws_config;
		case "development":
			return dev_aws_config;
		case "newfeature":
			return newfeature_aws_config;
		case "newfeature2":
			return newfeature2_aws_config;
		default:
			return local_aws_config;
	}
})();

function configureAmplify() {
	Amplify.configure(aws_config_for_environment, {
		API: {
			REST: {
				retryStrategy: {
					strategy: "no-retry", // Overrides default retry strategy, which will retry 500 responses 3x
				},
			},
		},
	});
}

export { aws_config_for_environment, configureAmplify };
